'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Share2, Bookmark, Copy, Facebook, Check, Linkedin, X } from 'lucide-react';

interface ArticleActionsProps {
    url: string;
    title: string;
}

export default function ArticleActions({ url, title }: ArticleActionsProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [bookmarked, setBookmarked] = useState(false);
    const [copied, setCopied] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        const bookmarks = JSON.parse(localStorage.getItem('muktokontho_bookmarks') || '[]');
        if (bookmarks.includes(url)) {
            setBookmarked(true);
        }
    }, [url]);

    if (!mounted) return null;

    const handleBookmark = () => {
        const bookmarks = JSON.parse(localStorage.getItem('muktokontho_bookmarks') || '[]');
        let newBookmarks;

        if (bookmarked) {
            newBookmarks = bookmarks.filter((b: string) => b !== url);
        } else {
            newBookmarks = [...bookmarks, url];
        }

        localStorage.setItem('muktokontho_bookmarks', JSON.stringify(newBookmarks));
        setBookmarked(!bookmarked);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy', err);
        }
    };

    const shareLinks = [
        {
            name: 'Facebook',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            icon: Facebook,
            color: 'bg-[#1877F2]',
            textColor: 'text-white'
        },
        {
            name: 'X (Twitter)',
            href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
            icon: ({ className }: { className?: string }) => (
                <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            ),
            color: 'bg-black',
            textColor: 'text-white'
        },
        {
            name: 'LinkedIn',
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
            icon: Linkedin,
            color: 'bg-[#0077b5]',
            textColor: 'text-white'
        }
    ];

    return (
        <>
            {/* Trigger Button - Floating Bottom Right for Mobile, Sticky Sidebar for Desktop */}
            <div className="fixed bottom-6 right-6 z-[90] lg:hidden">
                <button
                    onClick={() => setIsOpen(true)}
                    className="w-14 h-14 bg-slate-900 text-white rounded-full shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
                >
                    <Share2 className="w-6 h-6" />
                </button>
            </div>

            <div className="hidden lg:block sticky top-32 z-30">
                <button
                    onClick={() => setIsOpen(true)}
                    className="w-12 h-12 bg-white border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 rounded-2xl shadow-premium flex items-center justify-center transition-all group relative"
                >
                    <Share2 className="w-5 h-5" />
                    <span className="absolute left-14 px-3 py-1 bg-slate-900 text-white text-xs font-bold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        Share Article
                    </span>
                </button>
            </div>

            {/* Modal Overlay - Rendered in Portal to escape stacking contexts */}
            {mounted && createPortal(
                <>
                    {isOpen && (
                        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                            {/* Backdrop */}
                            <div
                                onClick={() => setIsOpen(false)}
                                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in-up"
                                style={{ animationDuration: '300ms' }}
                            />

                            {/* Modal Content */}
                            <div
                                className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden z-10 animate-fade-in-up"
                                style={{ animationDuration: '400ms' }}
                            >
                                {/* Header */}
                                <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                    <h3 className="text-xl font-black text-slate-900 font-bengali">শেয়ার করুন</h3>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Body */}
                                <div className="p-8">
                                    <div className="grid grid-cols-3 gap-4 mb-8">
                                        {shareLinks.map((link) => (
                                            <a
                                                key={link.name}
                                                href={link.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex flex-col items-center gap-3 p-4 rounded-2xl transition-all hover:scale-105 active:scale-95 ${link.color} ${link.textColor} shadow-lg`}
                                            >
                                                <link.icon className="w-6 h-6" />
                                                <span className="text-xs font-bold">{link.name}</span>
                                            </a>
                                        ))}
                                    </div>

                                    <div className="space-y-3">
                                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between gap-4">
                                            <p className="text-sm font-medium text-slate-500 truncate flex-1 font-mono">
                                                {url}
                                            </p>
                                            <button
                                                onClick={handleCopy}
                                                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold shadow-sm hover:border-slate-300 transition-colors"
                                            >
                                                {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                                                {copied ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>

                                        <button
                                            onClick={handleBookmark}
                                            className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${bookmarked
                                                ? 'bg-red-50 text-red-600 border border-red-200'
                                                : 'bg-slate-900 text-white hover:bg-slate-800'
                                                }`}
                                        >
                                            <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current' : ''}`} />
                                            {bookmarked ? 'সংরক্ষিত তালিকা থেকে সরান' : 'পরবর্তীতে পড়ার জন্য সেভ করুন'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>,
                document.body
            )
            }
        </>
    );
}
